import React, { useEffect } from 'react';
import styled from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';
import pluralize from 'pluralize';

import { checkoutUrl } from './utils';
import {
  recordShow,
  recordDismissal,
  recordClickthrough,
} from 'js/utils/dismissibles';

type FilterCounts = {
  topRelated: number;
  sameAuthor: number;
  citingThis: number;
  citedByThis: number;
};

type Props = {
  filterCounts: FilterCounts;
  onClose: () => void;
  workId: number;
};

const Button = styled.button`
  align-items: center;
  background: #094ec3;
  border: 0;
  border-radius: 100px;
  color: #fff;
  display: flex;
  font-size: 15px;
  font-weight: 500;
  height: 36px;
  justify-content: center;
  width: 235px;

  &:hover {
    color: #fff;
    text-decoration: none;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 20px;
  justify-self: flex-end;
  padding: 11px 14px;
  position: absolute;
  right: 0;
  top: 0;
`;

const Container = styled.aside`
  background: #ffffff;
  border-radius: 8px;
  bottom: 33px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  left: 39px;
  overflow: hidden;
  position: fixed;
  max-width: 400px;
  z-index: 10;
`;

const Description = styled.div`
  display: flex;
  color: #333;
  font-size: 16px;
  margin: 0 0 16px;
`;

const PaperIcon = styled(Icon)`
  color: #a0abc1;
`;

const IconContainer = styled.div`
  border: 1px solid #d8d8d8;
  height: 60px;
  display: grid;
  flex-shrink: 0;
  place-items: center;
  margin-right: 16px;
  width: 60px;
`;

const InnerContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 16px 20px 24px;
`;

const Title = styled.h1`
  align-items: center;
  background: #094ec3;
  color: #fff;
  display: flex;
  font-size: 14px;
  font-weight: 900;
  height: 42px;
  justify-content: center;
  letter-spacing: 2px;
  margin: 0;
  text-transform: uppercase;
`;

// @ts-expect-error ts-migrate(7006) FIXME: Parameter 'filterCounts' implicitly has an 'any' t... Remove this comment to see the full error message
const getBody = (filterCounts) => {
  const USABLE_FILTERS = [
    'topRelated',
    'sameAuthor',
    'citingThis',
    'citedByThis',
  ];
  const totalCount = Object.keys(filterCounts)
    .filter((key) => USABLE_FILTERS.includes(key))
    .map((key) => filterCounts[key])
    .reduce((a, b) => a + b, 0);
  let text;

  if ('citingThis' in filterCounts && filterCounts['citingThis'] > 0) {
    text = filterCounts['citingThis'] + ' that cite it.';
  } else if ('citedByThis' in filterCounts && filterCounts['citedByThis'] > 0) {
    text = filterCounts['citedByThis'] + ' cited by it.';
  } else if ('topRelated' in filterCounts && filterCounts['topRelated'] > 0) {
    text = filterCounts['topRelated'] + ' top related papers.';
  } else if ('sameAuthor' in filterCounts && filterCounts['sameAuthor'] > 0) {
    text = filterCounts['sameAuthor'] + ' by the same author.';
  }

  return (
    <div className="text">
      With your download, get the{' '}
      <strong>
        {totalCount} best {pluralize('papers', totalCount)}
      </strong>{' '}
      relevant to this one
      {text ? ', including ' : ''}
      <strong>{text ? text : '.'}</strong>
    </div>
  );
};

const BulkDownloadPing = ({ filterCounts, onClose, workId }: Props) => {
  useEffect(() => {
    recordShow('BulkDownloadPing');
  }, []);

  const close = () => {
    recordDismissal('BulkDownloadPing');
    onClose();
  };

  const logClickthrough = () => {
    recordClickthrough('BulkDownloadPing');
  };

  return (
    <Container>
      <Title>Find the perfect paper</Title>
      <CloseButton aria-label="Close" onClick={close}>
        <Icon name="close" />
      </CloseButton>

      <InnerContainer>
        <Description>
          <IconContainer>
            <PaperIcon name="file_copy" size={26} />
          </IconContainer>
          {getBody(filterCounts)}
        </Description>

        <Button as="a" href={checkoutUrl(workId)} onClick={logClickthrough}>
          Upgrade For Bulk Download
        </Button>
      </InnerContainer>
    </Container>
  );
};

export default BulkDownloadPing;
